import { useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { reportMoney, reportMonthToDate, reportMonthWindow, reportNumber, runReport, type ReportResult } from '../lib/reports';
import { dayLabel } from './useControlCentreReports';

export interface LeadShare {
  name: string;
  count: number;
  value: number;
  share: number;
}

export type LeadFeedStatus = 'Open' | 'Won' | 'Lost';

export interface LeadFeedItem {
  id: number;
  title: string;
  subtitle: string;
  value: number;
  status: LeadFeedStatus;
  day: string;
  dayLabel: string;
  isToday: boolean;
}

export interface YtdMonth {
  month: string;
  label: string;
  isCurrent: boolean;
  newLeads: number;
  conversion: number;
  winRate: number;
  avgDeal: number;
  timeToClose: number;
  revenue: number;
}

export interface LeadsReportDetails {
  loading: boolean;
  error: string | null;
  overdue: number;
  stalled: number;
  outcomes: LeadShare[];
  conversion: number;
  wonSources: LeadShare[];
  lostReasons: LeadShare[];
  openPipeline: number;
  winRate: number;
  winRateLast: number;
  revenue: number;
  revenueLast: number;
  avgDeal: number;
  avgDealLast: number;
  timeToClose: number;
  timeToCloseLast: number;
  timeToContact: number;
  timeToContactLast: number;
  feed: LeadFeedItem[];
  ytd: YtdMonth[];
}

const empty: LeadsReportDetails = {
  loading: false,
  error: null,
  overdue: 0,
  stalled: 0,
  outcomes: [],
  conversion: 0,
  wonSources: [],
  lostReasons: [],
  openPipeline: 0,
  winRate: 0,
  winRateLast: 0,
  revenue: 0,
  revenueLast: 0,
  avgDeal: 0,
  avgDealLast: 0,
  timeToClose: 0,
  timeToCloseLast: 0,
  timeToContact: 0,
  timeToContactLast: 0,
  feed: [],
  ytd: [],
};

const OUTCOME_ORDER = ['Open', 'Won', 'Lost'];

function rowNumber(result: ReportResult, row: ReportResult['rows'][number], metric: string): number {
  return reportNumber({ ...result, rows: [row] }, metric);
}

function rowMoney(result: ReportResult, row: ReportResult['rows'][number], metric: string): number {
  return reportMoney({ ...result, rows: [row] }, metric);
}

function shares(result: ReportResult, dimension: string, countMetric: string, valueMetric: string): LeadShare[] {
  const rows = result.rows.map((row) => ({
    name: String(row[dimension] || 'Unknown'),
    count: rowNumber(result, row, countMetric),
    value: rowMoney(result, row, valueMetric),
  }));
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  return rows
    .filter((row) => row.count > 0)
    .map((row) => ({ ...row, share: total > 0 ? row.count / total : 0 }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

function winRate(won: number, lost: number): number {
  const closed = won + lost;
  return closed > 0 ? (won / closed) * 100 : 0;
}

function eachMonth(since: string, until: string): string[] {
  const months: string[] = [];
  let year = Number(since.slice(0, 4));
  let month = Number(since.slice(5, 7));
  const end = until.slice(0, 7);
  while (`${year}-${String(month).padStart(2, '0')}` <= end && months.length < 12) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

function monthShort(key: string): string {
  return new Date(`${key}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
}

function feedStatus(value: string): LeadFeedStatus {
  if (value === 'Won' || value === 'Lost') return value;
  return 'Open';
}

/**
 * The rest of the New leads expanded view: cohort outcomes, won-by-source,
 * lost reasons, pipeline health, the lead list, and year-to-date trends.
 * Loads from the leads report when the modal opens.
 */
export function useLeadsReportDetails(open: boolean, openPipeline: number): LeadsReportDetails {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || 'America/Vancouver';
  const [details, setDetails] = useState<LeadsReportDetails>(empty);

  useEffect(() => {
    if (!open || !businessId) return;
    let cancelled = false;
    setDetails((current) => ({ ...current, loading: true, error: null }));
    const mtd = reportMonthToDate(timeZone);
    const last = reportMonthWindow(timeZone, -1);
    const yearStart = `${mtd.until.slice(0, 4)}-01-01`;
    const health = `deals_won, deals_lost, revenue_won, average_won_value, time_to_close_days, time_to_first_contact_days, overdue_follow_ups, stalled_leads`;

    void Promise.all([
      runReport(businessId, `FROM leads SHOW ${health} SINCE ${mtd.since} UNTIL ${mtd.until}`),
      runReport(businessId, `FROM leads SHOW deals_won, deals_lost, revenue_won, average_won_value, time_to_close_days, time_to_first_contact_days SINCE ${last.since} UNTIL ${last.until}`),
      runReport(businessId, `FROM leads SHOW leads, pipeline_value GROUP BY status SINCE ${mtd.since} UNTIL ${mtd.until}`),
      runReport(businessId, `FROM leads SHOW won, won_value GROUP BY won_source SINCE ${mtd.since} UNTIL ${mtd.until}`),
      runReport(businessId, `FROM leads SHOW lost, pipeline_value GROUP BY closed_reason SINCE ${mtd.since} UNTIL ${mtd.until}`),
      runReport(businessId, `FROM leads SHOW pipeline_value GROUP BY lead SINCE ${mtd.since} UNTIL ${mtd.until}`),
      runReport(businessId, `FROM leads SHOW leads, won, conversion_rate GROUP BY month SINCE ${yearStart} UNTIL ${mtd.until}`),
      runReport(businessId, `FROM leads SHOW won, lost, won_value, time_to_close_days GROUP BY closed_month SINCE ${yearStart} UNTIL ${mtd.until}`),
    ])
      .then(([now, previous, status, wonSources, lostReasons, leads, createdMonths, closedMonths]) => {
        if (cancelled) return;
        const outcomeRows = status.rows
          .map((row) => ({
            name: String(row.status || 'Open'),
            count: rowNumber(status, row, 'leads'),
            value: rowMoney(status, row, 'pipeline_value'),
          }))
          .filter((row) => row.count > 0);
        const outcomeTotal = outcomeRows.reduce((sum, row) => sum + row.count, 0);
        const outcomeWon = outcomeRows.find((row) => row.name === 'Won')?.count ?? 0;
        const createdByMonth = new Map(createdMonths.rows.map((row) => [String(row.month), row]));
        const closedByMonth = new Map(closedMonths.rows.map((row) => [String(row.closed_month), row]));
        const currentMonth = mtd.until.slice(0, 7);

        setDetails({
          loading: false,
          error: null,
          overdue: reportNumber(now, 'overdue_follow_ups'),
          stalled: reportNumber(now, 'stalled_leads'),
          outcomes: OUTCOME_ORDER
            .map((name) => outcomeRows.find((row) => row.name === name))
            .filter((row): row is { name: string; count: number; value: number } => !!row)
            .map((row) => ({ ...row, share: outcomeTotal > 0 ? row.count / outcomeTotal : 0 })),
          conversion: outcomeTotal > 0 ? (outcomeWon / outcomeTotal) * 100 : 0,
          wonSources: shares(wonSources, 'won_source', 'won', 'won_value'),
          lostReasons: shares(lostReasons, 'closed_reason', 'lost', 'pipeline_value'),
          openPipeline,
          winRate: winRate(reportNumber(now, 'deals_won'), reportNumber(now, 'deals_lost')),
          winRateLast: winRate(reportNumber(previous, 'deals_won'), reportNumber(previous, 'deals_lost')),
          revenue: reportMoney(now, 'revenue_won'),
          revenueLast: reportMoney(previous, 'revenue_won'),
          avgDeal: reportMoney(now, 'average_won_value'),
          avgDealLast: reportMoney(previous, 'average_won_value'),
          timeToClose: reportNumber(now, 'time_to_close_days'),
          timeToCloseLast: reportNumber(previous, 'time_to_close_days'),
          timeToContact: reportNumber(now, 'time_to_first_contact_days'),
          timeToContactLast: reportNumber(previous, 'time_to_first_contact_days'),
          feed: leads.rows.map((row) => {
            const day = String(row.day || '');
            return {
              id: Number(row.opportunity_id) || 0,
              title: String(row.lead || 'Untitled lead'),
              subtitle: String(row.subtitle || 'No source recorded'),
              value: rowMoney(leads, row, 'pipeline_value'),
              status: feedStatus(String(row.status || '')),
              day,
              dayLabel: day ? dayLabel(day) : '',
              isToday: day === mtd.until,
            };
          }),
          ytd: eachMonth(yearStart, mtd.until).map((month) => {
            const created = createdByMonth.get(month);
            const closed = closedByMonth.get(month);
            const won = closed ? rowNumber(closedMonths, closed, 'won') : 0;
            const lost = closed ? rowNumber(closedMonths, closed, 'lost') : 0;
            const revenue = closed ? rowMoney(closedMonths, closed, 'won_value') : 0;
            return {
              month,
              label: monthShort(month),
              isCurrent: month === currentMonth,
              newLeads: created ? rowNumber(createdMonths, created, 'leads') : 0,
              conversion: created ? rowNumber(createdMonths, created, 'conversion_rate') : 0,
              winRate: winRate(won, lost),
              avgDeal: won > 0 ? revenue / won : 0,
              timeToClose: closed ? rowNumber(closedMonths, closed, 'time_to_close_days') : 0,
              revenue,
            };
          }),
        });
      })
      .catch((err: unknown) => {
        console.error('Leads report details:', err);
        if (!cancelled) {
          setDetails({
            ...empty,
            loading: false,
            openPipeline,
            error: 'Some of this report didn’t load. Close it and open it again.',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, businessId, timeZone, openPipeline]);

  return details;
}
